import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const MediaSection = ({ formData, setFormData, errors }) => {
  const handleHeroImageUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          heroImage: file,
          heroImagePreview: e?.target?.result
        }));
      };
      reader?.readAsDataURL(file);
    }
  };

  const removeHeroImage = () => {
    setFormData(prev => ({
      ...prev,
      heroImage: null,
      heroImagePreview: null
    }));
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Image" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Media & Images
            </h2>
            <p className="text-muted-foreground text-sm">
              Showcase your business with beautiful images
            </p>
          </div>
        </div>

        {/* Hero Image Section */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Hero Image
              <span className="text-muted-foreground text-xs ml-1">(recommended)</span>
            </label>
            
            {/* Hero Image Preview */}
            <div className="relative">
              {formData?.heroImagePreview ? (
                <div className="relative group">
                  <img
                    src={formData?.heroImagePreview}
                    alt="Hero preview"
                    className="w-full h-48 object-cover rounded-lg border border-border"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                    <div className="flex space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => document.getElementById('hero-upload')?.click()}
                        iconName="RefreshCw"
                        iconPosition="left"
                      >
                        Replace
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={removeHeroImage}
                        iconName="Trash2"
                        iconPosition="left"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => document.getElementById('hero-upload')?.click()}
                  className="w-full h-48 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center bg-muted/50 hover:bg-muted/70 transition-colors cursor-pointer"
                >
                  <Icon name="Image" size={32} className="text-muted-foreground mb-2" />
                  <p className="text-sm font-medium text-foreground">Upload Hero Image</p>
                  <p className="text-xs text-muted-foreground">
                    This will appear at the top of your business page
                  </p>
                </div>
              )}

              <input
                type="file"
                id="hero-upload"
                accept="image/*"
                onChange={handleHeroImageUpload}
                className="hidden"
              />
            </div>

            <div className="mt-2 flex items-start space-x-2">
              <Icon name="Info" size={14} className="text-muted-foreground mt-0.5" />
              <div className="text-xs text-muted-foreground">
                <p>Recommended size: 1200x400px</p>
                <p>PNG or JPG, up to 5MB</p>
                <p>This image will be displayed prominently on your business detail page</p>
              </div>
            </div>
          </div>

          {/* Upload Actions */}
          {!formData?.heroImagePreview && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={() => document.getElementById('hero-upload')?.click()}
                iconName="Upload"
                iconPosition="left"
              >
                Upload Hero Image
              </Button>
            </div>
          )}
        </div>

        {/* Helpful Tips */}
        <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon name="Lightbulb" size={12} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100 text-sm">
                Image Tips
              </h4>
              <ul className="text-xs text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                <li>• Use high-quality images that represent your business well</li>
                <li>• Show your space, practitioners, or services in action</li>
                <li>• Avoid cluttered or overly busy images</li>
                <li>• Ensure images are well-lit and professional</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Current Media Summary */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground text-sm">Media Summary</h4>
              <div className="flex items-center space-x-4 mt-1">
                <div className="flex items-center space-x-1">
                  <Icon 
                    name={formData?.logoPreview ? "CheckCircle" : "Circle"} 
                    size={14} 
                    className={formData?.logoPreview ? "text-green-500" : "text-muted-foreground"} 
                  />
                  <span className="text-xs text-muted-foreground">Logo</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon 
                    name={formData?.heroImagePreview ? "CheckCircle" : "Circle"} 
                    size={14} 
                    className={formData?.heroImagePreview ? "text-green-500" : "text-muted-foreground"} 
                  />
                  <span className="text-xs text-muted-foreground">Hero Image</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">
                {(formData?.logoPreview ? 1 : 0) + (formData?.heroImagePreview ? 1 : 0)} of 2 images uploaded
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaSection;